import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PieChartComponent } from '../../../shared/components/charts/pie-chart/pie-chart.component';
import { BarChartOneComponent } from '../../../shared/components/charts/bar/bar-chart-one/bar-chart-one.component';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { DashboardService, DashboardOverview } from '../../../services/dashboard/dashboard.service';
import { AppDatePipe } from '../../../shared/pipe/app-date.pipe';

@Component({
  selector: 'app-ecommerce',
  imports: [CommonModule, AppDatePipe, BarChartOneComponent, PieChartComponent, TooltipModule],
  providers: [MessageService],
  templateUrl: './ecommerce.component.html',
  styleUrl: './ecommerce.component.css',
})
export class EcommerceComponent implements OnInit {
  loading = false;
  errorMessage = '';
  userName = 'there';
  today = new Date();

  stats: any[] = [];
  trendSeries: any[] = [];
  trendLabels: string[] = [];

  statusSeries: number[] = [];
  statusLabels: string[] = [];
  statusColors = ['#22C55E', '#06B6D4', '#F59E0B', '#EF4444'];
  statusTotal = '0';

  statusTrendLabels: string[] = [];
  statusTrendSeries: any[] = [];
  statusTrendColors: string[] = [];

  protocolColors = ['#465FFF', '#22C55E', '#F59E0B', '#EF4444', '#A855F7'];

  fileSeries: number[] = [];
  fileLabels: string[] = [];
  fileColors = ['#465FFF', '#A855F7', '#06B6D4', '#22C55E', '#F59E0B'];

  messageSeries: number[] = [];
  messageLabels: string[] = [];
  messageColors = ['#465FFF', '#22C55E', '#EF4444', '#F59E0B', '#A855F7', '#06B6D4'];

  protocolSeries: any[] = [];
  protocolLabels: string[] = [];

  ackSeries: number[] = [];
  ackLabels: string[] = [];
  ackColors = ['#22C55E', '#F59E0B', '#EF4444'];

  topPartners: { name: string; count: number }[] = [];
  recentActivity: any[] = [];

  constructor(
    private dashboardService: DashboardService,
    private messageService: MessageService
  ) {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    this.userName = userDetails?.first_name || 'there';
  }

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.loading = true;
    this.errorMessage = '';
    this.dashboardService.getDashboardOverview().subscribe({
      next: (data) => this.applyData(data),
      error: (err) => {
        this.loading = false;
        this.errorMessage = err?.message || 'Failed to load dashboard data';
        this.messageService.add({ severity: 'error', summary: 'Error', life: 4000, detail: this.errorMessage });
      }
    });
  }

  applyData(data: DashboardOverview) {
    this.loading = false;

    const formatKpi = (value: number) => value >= 10000 ? (value / 1000).toFixed(1) + 'k' : value.toLocaleString('en-US');

    this.statusLabels = data.by_status.map((item) => item.label);
    this.statusSeries = data.by_status.map((item) => item.value);
    this.statusTotal = data.kpis.total_documents.value.toLocaleString('en-US');

    this.stats = [
      {
        title: 'Documents Processed',
        value: formatKpi(data.kpis.total_documents.value),
        description: 'Documents processed across all channels',
        theme: 'blue',
        icon: 'pi-send'
      },
      {
        title: 'Success Rate',
        value: data.kpis.success_rate.value + '%',
        description: 'Share of documents processed successfully',
        theme: 'green',
        icon: 'pi-check-circle'
      },
      {
        title: 'Failed',
        value: formatKpi(data.kpis.failed_count.value),
        description: 'Documents that failed processing',
        theme: 'red',
        icon: 'pi-times-circle'
      },
      {
        title: 'Active Partners',
        value: formatKpi(data.kpis.active_partners.value),
        description: 'Trading partners currently active',
        theme: 'amber',
        icon: 'pi-users'
      },
      {
        title: 'SLA Compliance',
        value: data.kpis.sla_compliance.value + '%',
        description: 'Documents meeting their SLA targets',
        theme: 'blue',
        icon: 'pi-clock'
      },
      {
        title: 'In-Process / Pending',
        value: formatKpi(data.kpis.in_process.value),
        description: 'Awaiting processing or acknowledgement',
        theme: 'amber',
        icon: 'pi-hourglass'
      }
    ];

    this.trendLabels = data.trend.labels;
    this.trendSeries = [
      { name: 'Inbound', data: data.trend.inbound },
      { name: 'Outbound', data: data.trend.outbound }
    ];

    this.statusTrendLabels = data.status_trend.labels;
    this.statusTrendSeries = data.status_trend.series;
    this.statusTrendColors = data.status_trend.series.map((series) =>
      this.statusTrendColorFor(series.name));

    this.fileLabels = data.by_file_format.map((item) => item.label);
    this.fileSeries = data.by_file_format.map((item) => item.value);

    this.messageLabels = data.by_message_type.map((item) => item.label);
    this.messageSeries = data.by_message_type.map((item) => item.value);

    this.protocolLabels = data.by_protocol.map((item) => item.label);
    this.protocolSeries = [{
      name: 'Documents',
      data: data.by_protocol.map((item) => item.value)
    }];

    this.ackLabels = data.ack_status.map((item) => item.label);
    this.ackSeries = data.ack_status.map((item) => item.value);

    this.topPartners = data.top_partners;
    this.recentActivity = data.recent_activity;
  }

  statusTrendColorFor(name: string): string {
    const value = name.toLowerCase();
    if (['error', 'fail', 'reject', 'discard'].some((kw) => value.includes(kw))) return '#EF4444';
    if (['wait', 'pending', 'on hold', 'warning'].some((kw) => value.includes(kw))) return '#F59E0B';
    if (['complete', 'success', 'valid', 'accepted', 'processed'].some((kw) => value.includes(kw))) return '#22C55E';
    if (['in progress', 'received', 'sending'].some((kw) => value.includes(kw))) return '#465FFF';
    return '#A855F7';
  }

  get maxPartnerCount(): number {
    return this.topPartners.reduce((max, partner) => Math.max(max, partner.count), 0) || 1;
  }

  getTotalOf(values: number[]): string {
    return values.reduce((sum, value) => sum + value, 0).toLocaleString('en-US');
  }
}
